/* eslint-disable */
// useNotificationChannel — subscribes the signed-in user to their own private
// Broadcast topic and refetches when something lands.
//
// ⚠️ NOT an extension of useRealtimeSubscription.js. That hook wraps
// postgres_changes, a different mechanism, and its API is load-bearing for the
// Developments subscription. This is Broadcast, so it is a new hook with a new
// name — the Convention #37 precedent (build a new hook, never branch an
// existing one with a mode prop).
//
// THE MESSAGE IS A POINTER, NOT CONTENT. The trigger sends notification_id and
// severity and nothing else — no title, no body. If it carried them, the RLS on
// the notifications table would be irrelevant to that copy, because a broadcast
// payload is delivered without re-checking who may read the row. So on receipt
// this hook renders nothing from the payload: it invalidates the query and lets
// the normal fetch bring the content back through RLS.

import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '../supabase';
import { browserDay, firstTimeToday } from '../utils/sentryGate';

export function useNotificationChannel({ userId, onMessage, enabled = true }) {
  // Parent re-renders must not tear the channel down.
  const onMessageRef = useRef(onMessage);
  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);

  // ⚠️ StrictMode double-invokes effects in development. Without this guard the
  // second invocation subscribes a channel with the same topic and the first
  // one is closed underneath it, leaving a live-looking hook that receives
  // nothing.
  const subscribedFor = useRef(null);

  useEffect(() => {
    if (!enabled || !userId) return undefined;
    if (subscribedFor.current === userId) return undefined;
    subscribedFor.current = userId;

    let channel = null;
    let cancelled = false;

    const fire = () => {
      try { onMessageRef.current?.(); }
      catch (err) {
        Sentry.captureException(err, { tags: { source: 'useNotificationChannel.onMessage' } });
      }
    };

    const start = async () => {
      // A private channel is authorized against realtime.messages RLS, and that
      // check needs the user's JWT on the realtime socket — which is not the
      // same connection as PostgREST. Without this the subscribe is rejected.
      try {
        const { data } = await supabase.auth.getSession();
        const token = data?.session?.access_token;
        if (token) await supabase.realtime.setAuth(token);
      } catch (err) {
        Sentry.captureException(err, { tags: { source: 'useNotificationChannel.setAuth' } });
      }
      if (cancelled) return;

      // Topic must be exactly `notif:<uuid>` — the notif_topic_own policy
      // compares by EQUALITY, not by prefix.
      channel = supabase.channel(`notif:${userId}`, {
        // ⚠️ THE SINGLE MOST IMPORTANT LINE IN THIS FILE.
        // Without `private: true` Supabase does not run the RLS check on
        // realtime.messages AT ALL. The channel still works, the cards still
        // appear, and nothing looks wrong — but the stream is open rather than
        // authorized. This is the difference between a private topic and a
        // public one, and it fails silently in the permissive direction.
        config: { private: true },
      });

      channel
        .on('broadcast', { event: 'notification' }, fire)
        .subscribe((status, err) => {
          if (status === 'SUBSCRIBED') {
            Sentry.addBreadcrumb({
              category: 'realtime',
              level: 'info',
              message: 'notifications: SUBSCRIBED',
            });
            // Catch anything that landed between page load and this moment.
            fire();
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            // SYS-11: one Sentry event per browser per day; repeats are
            // breadcrumbs. The message string is unchanged so issues keep grouping.
            const message = `realtime.notifications.${status.toLowerCase()}`;
            if (firstTimeToday(message, browserDay())) {
              Sentry.captureMessage(message, {
                level: 'warning',
                tags: { realtime_status: status },
                extra: { error: err?.message },
              });
            } else {
              Sentry.addBreadcrumb({
                category: 'realtime',
                level: 'warning',
                message,
                data: { realtime_status: status, error: err?.message },
              });
            }
          }
        });
    };

    start();

    // ⚠️ A backgrounded tab loses its heartbeat and the socket can drop with no
    // error at all — no CHANNEL_ERROR, no TIMED_OUT, nothing. Someone who parks
    // a tab for a few minutes and comes back would simply never see anything
    // again. Refetching on return to visible is what covers that gap; it is not
    // an optimisation.
    const onVisible = () => { if (document.visibilityState === 'visible') fire(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      subscribedFor.current = null;
      if (channel) {
        // Missing cleanup is the documented cause of too_many_channels.
        try { channel.unsubscribe(); } catch (_) {}
        supabase.removeChannel(channel);
      }
    };
  }, [userId, enabled]);
}
